import type { CSSObject } from '@mui/material/styles'
import { Components, Theme, TypographyVariantsOptions } from '@mui/material/styles'

// Types for color objects
export interface ColorVariant {
  DEFAULT?: string
  main?: string
  dark?: string
  light?: string
}

export interface InputColors {
  text: ColorVariant
  background: ColorVariant
  placeholder: ColorVariant
  border: {
    color: ColorVariant
    focus: {
      color: ColorVariant
    }
  }
}

export interface ThemeColors {
  primary: ColorVariant
  secondary: ColorVariant
  third: ColorVariant
  info: ColorVariant
  four: ColorVariant // warning
  five: ColorVariant
  six: ColorVariant
  success: ColorVariant
  error: ColorVariant
  active: ColorVariant
  pending: ColorVariant
  action: ColorVariant
  mbg: ColorVariant
  mbgSecond: ColorVariant
  bd: ColorVariant
  input: InputColors
}

export type ThemeMode = 'light' | 'dark'

// Helper function to get theme-based colors
export const getThemeColor = (
  colorObj: ColorVariant,
  mode: ThemeMode,
  fallback: keyof ColorVariant = 'DEFAULT'
): string => {
  return mode === 'dark'
    ? colorObj?.dark || colorObj?.[fallback] || colorObj?.DEFAULT || '#000000'
    : colorObj?.[fallback] || colorObj?.DEFAULT || colorObj?.main || '#ffffff'
}

// Helper function to create palette colors
export const createPaletteColor = (colorObj: ColorVariant) => ({
  main: colorObj?.main || colorObj?.DEFAULT || '#000000',
  dark: colorObj?.dark || colorObj?.main || colorObj?.DEFAULT || '#000000',
  light: colorObj?.light || colorObj?.main || colorObj?.DEFAULT || '#ffffff',
})

export const createPaletteColorBg = (colorObj: ColorVariant, mode: ThemeMode) => ({
  main:
    mode === 'dark'
      ? colorObj?.dark || colorObj?.DEFAULT || '#000000'
      : colorObj?.main || colorObj?.DEFAULT || '#ffffff',
  dark: colorObj?.dark || colorObj?.main || colorObj?.DEFAULT || '#ffffff',
  light: colorObj?.light || colorObj?.main || colorObj?.DEFAULT || '#ffffff',
})

// Helper function to create component styles based on theme
export const createComponentStyles = (
  themeColors: ThemeColors,
  themeMode: ThemeMode
): Components<Omit<Theme, 'components'>> => {
  const { primary, secondary, input, mbgSecond: mbgSecond, bd, mbg } = themeColors

  return {
    MuiCssBaseline: {
      styleOverrides: `
        // * {
        //   transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease !important;
        // }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px ${getThemeColor(input.background, themeMode)} inset !important;
          -webkit-text-fill-color: ${getThemeColor(input.text, themeMode)} !important;
        }
        ::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
      `,
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 40,
          color: getThemeColor(input.text, themeMode),
          borderRadius: 99999,
          backgroundColor: getThemeColor(input.background, themeMode),
          '@media (max-width:768px)': {
            fontSize: '14px',
          },
          '& .MuiInputBase-input': {
            padding: '12px 14px',
            color: getThemeColor(input.text, themeMode),
            borderRadius: 99999,
            '&::placeholder': {
              color: getThemeColor(input.placeholder, themeMode),
              opacity: 1,
            },
            '&:-webkit-autofill': {
              height: '40px',
              boxSizing: 'border-box',
              borderRadius: 99999,
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: getThemeColor(input.border.color, themeMode),
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primary.main,
            transition: 'border-color 0.3s ease',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: input.border.focus.color.DEFAULT,
            transition: 'border-color 0.3s ease',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: primary.main,
          color: '#ffffff',
          '@media (min-width:768px)': {
            '&:hover': {
              backgroundColor: primary.dark,
            },
          },
        },
        containedSecondary: {
          backgroundColor: secondary.main,
          color: '#ffffff',
          '@media (min-width:768px)': {
            '&:hover': {
              backgroundColor: secondary.dark,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: getThemeColor(mbg, themeMode),
          borderColor: getThemeColor(bd, themeMode),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: getThemeColor(mbg, themeMode),
          color: getThemeColor(input.text, themeMode),
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: getThemeColor(mbgSecond, themeMode),
          borderColor: getThemeColor(bd, themeMode),
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // color: getThemeColor(input.text, themeMode),
          transition: 'color 0.3s ease',
          // Global hover styles for Typography components
          '&.hv-primary:hover': {
            color: getThemeColor(themeColors.active, themeMode),
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.MuiCheckbox-root': {
            marginRight: '.3em',
            borderRadius: '3px',
          },
          '& .MuiSvgIcon-root': {
            fontSize: '.8em',
            width: '1em',
            height: '1em',
            transform: 'scale(1.1)',
          },

          '&.Mui-checked .MuiSvgIcon-root': {
            scale: '1.2',
          },
        },
      },
    },
  }
}

// Helper function to create typography configuration
export const createTypographyConfig = (
  fontFamily: TypographyVariantsOptions['fontFamily'],
  baseFontSize: number
): TypographyVariantsOptions => ({
  fontFamily: fontFamily,
  // h1: {
  //   fontSize: pxToRem(baseFontSize * 2.5), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 4), // Desktop size
  //   },
  // },
  // h2: {
  //   fontSize: pxToRem(baseFontSize * 2), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 3), // Desktop size
  //   },
  // },
  // h3: {
  //   fontSize: pxToRem(baseFontSize * 2.25), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 2.25), // Desktop size
  //   },
  // },
  // h4: {
  //   fontSize: pxToRem(baseFontSize * 1.5), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 1.875), // Desktop size
  //   },
  // },
  // h5: {
  //   fontSize: pxToRem(baseFontSize * 1.25), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 1.5), // Desktop size
  //   },
  // },
  // h6: {
  //   fontSize: pxToRem(baseFontSize * 1.125), // Mobile size
  //   lineHeight: 1.5,
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 1.25), // Desktop size
  //   },
  // },
  // body1: {
  //   fontSize: pxToRem(baseFontSize * 0.9375), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize), // Desktop size
  //   },
  // },
  // body2: {
  //   fontSize: pxToRem(baseFontSize * 0.875), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 0.875), // Desktop size (unchanged)
  //   },
  // },
  // caption: {
  //   fontSize: pxToRem(baseFontSize * 0.75), // Mobile size
  //   '@media (min-width:600px)': {
  //     fontSize: pxToRem(baseFontSize * 0.75), // Desktop size (unchanged)
  //   },
  // },
  body2: {
    fontSize: '0.75rem', // Mobile size
    '@media (min-width:600px)': {
      fontSize: '0.875rem', // Desktop size (unchanged)
    },
  },
})

// Palette configuration interface
export interface PaletteConfig {
  mode: ThemeMode
  primary: {
    main: string
    dark: string
    light: string
  }
  secondary: {
    main: string
    dark: string
    light: string
  }
  info: {
    main: string
    dark: string
    light: string
  }
  warning: {
    main: string
    dark: string
    light: string
  }
  success: {
    main: string
    dark: string
    light: string
  }
  error: {
    main: string
    dark: string
    light: string
  }
  text: {
    primary: string
    secondary: string
  }
  background: {
    default: string
    paper: string
  }
  action: {
    active: string
    hover: string
    selected: string
    disabled: string
    disabledBackground: string
  }
  divider: string
  // Custom colors
  activeColor: {
    main: string
    dark: string
    light: string
  }
}

// Helper function to create palette configuration
export const createPaletteConfig = (themeColors: ThemeColors, themeMode: ThemeMode) => {
  const {
    primary,
    secondary,
    third,
    info,
    four: warning,
    five,
    six,
    success,
    error,
    active,
    pending,
    action,
    mbg,
    mbgSecond,
    bd,
    input,
  } = themeColors

  return {
    mode: themeMode,
    primary: createPaletteColor(primary),
    secondary: createPaletteColor(secondary),
    info: createPaletteColor(info),
    warning: createPaletteColor(warning),
    success: createPaletteColor(success),
    error: createPaletteColor(error),
    // Custom colors
    third: createPaletteColor(third),
    four: createPaletteColor(warning),
    five: createPaletteColor(five),
    six: createPaletteColor(six),
    active: createPaletteColor(active),
    pending: createPaletteColor(pending),
    actionColor: createPaletteColor(action),
    mbg: createPaletteColorBg(mbg, themeMode),
    mbgSecond: createPaletteColorBg(mbgSecond, themeMode),
    bd: createPaletteColorBg(bd, themeMode),
    // Text colors based on theme mode
    text: {
      primary: getThemeColor(input.text, themeMode),
      secondary: getThemeColor(input.placeholder, themeMode),
    },
    // Background colors based on theme mode
    background: {
      default: getThemeColor(mbg, themeMode),
      paper: getThemeColor(mbgSecond, themeMode),
    },
    // Action colors
    action: {
      active: primary.main || primary.DEFAULT || '#252522',
      hover: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
      selected: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
      disabled: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.26)' : 'rgba(0, 0, 0, 0.26)',
      disabledBackground: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
    },
    // Divider color
    divider: getThemeColor(bd, themeMode),
  }
}

export const createBreakpoints = () => ({
  values: {
    xs: 0,
    xsm: 400,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    xxl: 1536,
  },
  unit: 'px',
})

export type MaxLineProps = {
  line: number
  persistent?: Partial<React.CSSProperties>
}

export function lineClamp({ line }: MaxLineProps): CSSObject {
  const baseStyles: CSSObject = {
    overflow: 'hidden',
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    WebkitLineClamp: line,
    WebkitBoxOrient: 'vertical',
  }

  return baseStyles
}

export function maskBankAccount(accountNumber: string): string {
  const len = accountNumber.length

  if (len <= 2) {
    // quá ngắn → giữ nguyên
    return accountNumber
  }

  if (len <= 5) {
    // 3–5 ký tự → hiện 1 đầu 1 cuối
    const first = accountNumber[0]
    const last = accountNumber[len - 1]
    const hidden = '*'.repeat(len - 2)
    return `${first}${hidden}${last}`
  }

  if (len === 6) {
    // 6 ký tự → hiện 2 đầu 1 cuối
    const firstTwo = accountNumber.slice(0, 2)
    const last = accountNumber[len - 1]
    const hidden = '*'.repeat(len - 3)
    return `${firstTwo}${hidden}${last}`
  }

  // >6 ký tự → hiện 2 đầu và 2 cuối
  const firstTwo = accountNumber.slice(0, 2)
  const lastTwo = accountNumber.slice(-2)
  const hidden = '*'.repeat(len - 4)
  return `${firstTwo}${hidden}${lastTwo}`
}
